/* Boutique des Poignées - interactions (en-tête, menus, recherche, ajout rapide, animations) */
(() => {
  const html = document.documentElement;
  const isDesktop = () => window.matchMedia('(min-width: 1100px)').matches;
  const lockScroll = (on) => html.classList.toggle('bdp-lock', on);

  /* ---------- Barre d'annonce ---------- */
  function initAnnouncement(root) {
    const items = [...root.querySelectorAll('.bdp-announce__item')];
    if (items.length < 2) return;
    let index = 0;
    let timer;
    const delay = parseInt(root.dataset.speed || '5', 10) * 1000;
    const show = (i) => {
      items[index].classList.remove('is-active');
      items[index].setAttribute('aria-hidden', 'true');
      index = (i + items.length) % items.length;
      items[index].classList.add('is-active');
      items[index].removeAttribute('aria-hidden');
    };
    const play = () => { clearInterval(timer); timer = setInterval(() => show(index + 1), delay); };
    root.querySelector('[data-prev]')?.addEventListener('click', () => { show(index - 1); play(); });
    root.querySelector('[data-next]')?.addEventListener('click', () => { show(index + 1); play(); });
    root.addEventListener('mouseenter', () => clearInterval(timer));
    root.addEventListener('mouseleave', play);
    play();
  }

  /* ---------- En-tête ---------- */
  function initHeader(header) {
    if (!header || header.dataset.ready) return;
    header.dataset.ready = 'true';
    const overlay = document.querySelector('.bdp-header-overlay');

    // Ombre au défilement
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Méga-menu
    const items = [...header.querySelectorAll('.bdp-nav__item--mega')];
    let closeTimer;
    const closeAll = () => {
      items.forEach((item) => {
        item.classList.remove('is-open');
        item.querySelector('.bdp-nav__link')?.setAttribute('aria-expanded', 'false');
      });
      header.classList.remove('is-menu-open');
      overlay?.classList.remove('is-visible');
    };
    let openedAt = 0;
    const open = (item) => {
      clearTimeout(closeTimer);
      if (!item.classList.contains('is-open')) openedAt = Date.now();
      items.forEach((other) => other !== item && other.classList.remove('is-open'));
      item.classList.add('is-open');
      item.querySelector('.bdp-nav__link')?.setAttribute('aria-expanded', 'true');
      header.classList.add('is-menu-open');
      overlay?.classList.add('is-visible');
    };
    items.forEach((item) => {
      const trigger = item.querySelector('.bdp-nav__link');
      item.addEventListener('mouseenter', () => isDesktop() && open(item));
      item.addEventListener('mouseleave', () => {
        if (!isDesktop()) return;
        closeTimer = setTimeout(closeAll, 140);
      });
      trigger?.addEventListener('click', (event) => {
        if (trigger.tagName === 'BUTTON') {
          event.preventDefault();
          // Un clic juste après l'ouverture au survol ne doit pas refermer le menu
          if (item.classList.contains('is-open') && Date.now() - openedAt > 400) closeAll();
          else open(item);
        }
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) item.classList.remove('is-open');
      });
    });
    header.querySelectorAll('.bdp-nav__item:not(.bdp-nav__item--mega)').forEach((item) =>
      item.addEventListener('mouseenter', () => isDesktop() && closeAll())
    );
    overlay?.addEventListener('click', closeAll);
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openItem = items.find((item) => item.classList.contains('is-open'));
      if (openItem) {
        closeAll();
        openItem.querySelector('.bdp-nav__link')?.focus();
      }
    });
  }

  /* ---------- Tiroirs (menu mobile, recherche) ---------- */
  function initPanel(panel, { onOpen } = {}) {
    if (!panel || panel.dataset.ready) return;
    panel.dataset.ready = 'true';
    let lastTrigger = null;
    const openPanel = (trigger) => {
      lastTrigger = trigger || document.activeElement;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      document.querySelectorAll(`[aria-controls="${panel.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      onOpen?.();
      setTimeout(() => panel.querySelector('[data-autofocus]')?.focus() || panel.querySelector('button, a, input')?.focus(), 60);
    };
    const closePanel = () => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      document.querySelectorAll(`[aria-controls="${panel.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      lastTrigger?.focus?.();
    };
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest(`[aria-controls="${panel.id}"]`);
      if (trigger) {
        event.preventDefault();
        panel.classList.contains('is-open') ? closePanel() : openPanel(trigger);
      }
    });
    panel.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', closePanel));
    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closePanel();
      if (event.key !== 'Tab') return;
      const focusables = [...panel.querySelectorAll('a[href], button:not([disabled]), input, summary')].filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    window.addEventListener('resize', () => {
      if (panel.id === 'BdpMobileNav' && isDesktop() && panel.classList.contains('is-open')) closePanel();
    });
  }

  /* ---------- Recherche prédictive ---------- */
  function initSearch(panel) {
    if (!panel) return;
    const input = panel.querySelector('.bdp-search__input');
    const results = panel.querySelector('[data-results]');
    const defaults = panel.querySelector('[data-defaults]');
    if (!input || !results) return;
    let controller;
    let timer;
    const search = async (term) => {
      controller?.abort();
      if (term.length < 2) {
        results.hidden = true;
        results.innerHTML = '';
        if (defaults) defaults.hidden = false;
        return;
      }
      controller = new AbortController();
      try {
        const url = `${window.routes?.predictive_search_url || '/search/suggest'}?q=${encodeURIComponent(term)}&resources[type]=product,collection&resources[limit]=8&section_id=bdp-search-results`;
        const response = await fetch(url, { signal: controller.signal });
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const content = doc.querySelector('[data-bdp-search-results]');
        results.innerHTML = content ? content.innerHTML : '';
        results.hidden = false;
        if (defaults) defaults.hidden = true;
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      }
    };
    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => search(input.value.trim()), 220);
    });
  }

  /* ---------- Apparition au défilement ---------- */
  function initReveal(scope = document) {
    const elements = scope.querySelectorAll('.bdp-reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window)) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    elements.forEach((el) => observer.observe(el));
  }

  /* ---------- Ajout rapide au panier ---------- */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-bdp-add]');
    if (!button) return;
    event.preventDefault();
    const label = button.querySelector('[data-label]');
    const initial = label?.textContent;
    button.classList.add('is-loading');
    if (label) label.textContent = button.dataset.loadingText || '…';
    try {
      await window.bdpAddToCart([{ id: Number(button.dataset.variantId), quantity: 1 }], button);
      if (label) label.textContent = initial;
    } catch (error) {
      if (label) label.textContent = error.message || 'Erreur';
      setTimeout(() => { if (label) label.textContent = initial; }, 2600);
    }
    button.classList.remove('is-loading');
  });


  /* ---------- Onglets de sélection produits ---------- */
  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-bdp-tabs] [role="tab"]');
    if (!tab) return;
    const list = tab.closest('[data-bdp-tabs]');
    const section = list.closest('section');
    list.querySelectorAll('[role="tab"]').forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
    section.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
      panel.hidden = panel.id !== tab.getAttribute('aria-controls');
      if (!panel.hidden) {
        panel.querySelectorAll('.bdp-reveal').forEach((el) => el.classList.add('is-visible'));
        panel.querySelector('[data-track]')?.dispatchEvent(new Event('scroll'));
      }
    });
  });

  /* ---------- Galerie du produit vedette ---------- */
  document.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-bdp-gallery] [data-src]');
    if (!thumb) return;
    const gallery = thumb.closest('[data-bdp-gallery]');
    const main = gallery.querySelector('.bdp-feature__img');
    if (main) {
      main.removeAttribute('srcset');
      main.src = thumb.dataset.src;
    }
    gallery.querySelectorAll('[data-src]').forEach((b) => b.setAttribute('aria-current', String(b === thumb)));
  });


  /* ---------- Vitrine du hero (carrousel + étiquette produit) ---------- */
  function initVitrine(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const slides = [...root.querySelectorAll('.bdp-vitrine__slide')];
    const dots = [...root.querySelectorAll('[data-go]')];
    const chip = root.querySelector('[data-chip]');
    if (!slides.length) { chip?.remove(); return; }
    let index = 0;
    let timer;
    const show = (i) => {
      slides[index].classList.remove('is-active');
      index = (i + slides.length) % slides.length;
      const s = slides[index];
      s.classList.add('is-active');
      dots.forEach((d, n) => d.setAttribute('aria-current', String(n === index)));
      if (chip) {
        chip.href = s.dataset.url;
        chip.querySelector('[data-chip-img]').src = s.dataset.thumb;
        chip.querySelector('[data-chip-title]').textContent = s.dataset.title;
        chip.querySelector('[data-chip-price]').textContent = s.dataset.price;
      }
    };
    const play = () => {
      clearInterval(timer);
      if (slides.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        timer = setInterval(() => show(index + 1), parseInt(root.dataset.interval || '5000', 10));
      }
    };
    dots.forEach((d) => d.addEventListener('click', () => { show(parseInt(d.dataset.go, 10)); play(); }));
    root.addEventListener('mouseenter', () => clearInterval(timer));
    root.addEventListener('mouseleave', play);
    show(0);
    play();
  }

  /* ---------- Carrousels produits (flèches + progression) ---------- */
  function initSliders(scope) {
    scope.querySelectorAll('[data-bdp-slider]').forEach((panel) => {
      if (panel.dataset.ready) return;
      panel.dataset.ready = 'true';
      const track = panel.querySelector('[data-track]');
      const bar = panel.querySelector('[data-bar]');
      if (!track) return;
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const ratio = track.clientWidth / track.scrollWidth;
        if (bar) {
          bar.style.width = `${Math.min(100, ratio * 100)}%`;
          bar.style.transform = `translateX(${max > 0 ? (track.scrollLeft / max) * ((1 / ratio) - 1) * 100 : 0}%)`;
        }
      };
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }
  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('[data-slide]');
    if (!arrow) return;
    const section = arrow.closest('section');
    const panel = [...section.querySelectorAll('[data-bdp-slider]')].find((p) => !p.hidden);
    const track = panel?.querySelector('[data-track]');
    if (!track) return;
    const card = track.firstElementChild;
    const step = card ? card.getBoundingClientRect().width + 20 : track.clientWidth;
    track.scrollBy({ left: step * parseInt(arrow.dataset.slide, 10) * (window.innerWidth < 990 ? 1 : 2), behavior: 'smooth' });
  });

  /* ---------- Quiz ---------- */
  function initQuiz(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const steps = [...root.querySelectorAll('[data-step]')];
    const labels = [...root.querySelectorAll('[data-step-label]')];
    const back = root.querySelector('[data-back]');
    const counter = root.querySelector('[data-counter]');
    const progress = root.querySelector('[data-progress]');
    const results = root.querySelector('[data-results]');
    const note = root.querySelector('[data-note]');
    const answers = {};
    let current = 0;

    const go = (n) => {
      current = n;
      steps.forEach((s) => s.classList.toggle('is-active', parseInt(s.dataset.step, 10) === n));
      labels.forEach((l) => {
        const i = parseInt(l.dataset.stepLabel, 10);
        l.classList.toggle('is-current', i === n);
        l.classList.toggle('is-done', i < n);
      });
      if (progress) progress.style.width = `${(n / 3) * 100}%`;
      if (back) back.hidden = n === 0 || n === 3;
      if (counter) counter.textContent = n < 3 ? `Question ${n + 1} sur 3` : '';
      if (n === 3) loadResults();
    };

    const query = (keys) => keys.map((k) => answers[k]?.value).filter(Boolean).join(' ');

    const fetchProducts = async (q) => {
      const url = `${root.dataset.suggestUrl}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=last&section_id=bdp-quiz-results`;
      const html = await (await fetch(url)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const box = doc.querySelector('[data-bdp-quiz-results]');
      return { count: parseInt(box?.dataset.count || '0', 10), html: box ? box.innerHTML : '' };
    };

    const loadResults = async () => {
      const summary = root.querySelector('[data-summary]');
      summary.innerHTML = ['usage', 'material', 'color'].map((k) => (answers[k] ? `<span>${answers[k].label}</span>` : '')).join('');
      results.innerHTML = '<div class="bdp-quiz__loading">Nous cherchons les modèles faits pour vous…</div>';
      note.hidden = true;
      const attempts = [['usage', 'material', 'color'], ['usage', 'material'], ['usage', 'color'], ['material', 'color'], ['usage']];
      let found = null;
      let usedQuery = '';
      for (const keys of attempts) {
        const q = query(keys);
        if (!q) continue;
        try {
          const res = await fetchProducts(q);
          if (res.count > 0) { found = res; usedQuery = q; break; }
        } catch (e) { console.error(e); }
      }
      const exact = usedQuery === query(['usage', 'material', 'color']);
      const all = root.querySelector('[data-all]');
      if (found) {
        results.innerHTML = found.html;
        if (!exact) {
          note.hidden = false;
          note.textContent = 'Pas de correspondance exacte pour l’instant : voici les modèles les plus proches de votre recherche.';
        }
        all.href = `${root.dataset.searchUrl}?q=${encodeURIComponent(usedQuery)}&type=product&options%5Bprefix%5D=last`;
      } else {
        results.innerHTML = '<div class="bdp-quiz__loading">Aucun modèle ne correspond encore. Découvrez toute la collection ou écrivez-nous : nous trouverons la bonne poignée avec vous.</div>';
        all.href = '/collections/all';
      }
    };

    root.addEventListener('click', (event) => {
      const opt = event.target.closest('.bdp-opt');
      if (opt) {
        const key = opt.dataset.key;
        opt.parentElement.querySelectorAll('.bdp-opt').forEach((o) => o.setAttribute('aria-pressed', String(o === opt)));
        answers[key] = { value: opt.dataset.value, label: opt.dataset.label };
        setTimeout(() => go(current + 1), 220);
        return;
      }
      if (event.target.closest('[data-back]')) go(Math.max(0, current - 1));
      if (event.target.closest('[data-restart]')) {
        Object.keys(answers).forEach((k) => delete answers[k]);
        root.querySelectorAll('.bdp-opt').forEach((o) => o.setAttribute('aria-pressed', 'false'));
        go(0);
      }
    });
    go(0);
  }

  /* ---------- Calculateur d'entraxe ---------- */
  const ENTRAXES = [64, 96, 128, 160, 192, 224, 256, 320];
  const HINTS = {
    64: 'Format compact : petits tiroirs, portes de placard et meubles d’appoint.',
    96: 'Très répandu sur les tiroirs étroits et les meubles de salle de bain.',
    128: 'Le standard de la cuisine, à l’aise sur les façades de 40 à 60 cm.',
    160: 'Idéal pour les tiroirs de 60 cm et les portes de cuisine.',
    192: 'Pour les grands tiroirs et casseroliers : une prise franche et confortable.',
    224: 'Un effet linéaire et contemporain sur les tiroirs de 80 cm et plus.',
    256: 'Un effet linéaire et contemporain sur les tiroirs de 80 cm et plus.',
    320: 'Colonnes, armoires, portes hautes : la poignée devient un trait graphique.',
  };
  function initEntraxe(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const range = root.querySelector('[data-range]');
    const link = root.querySelector('[data-link]');
    const svg = root.querySelector('[data-entraxe-svg]');
    const el = (k) => svg?.querySelector(`[data-ex="${k}"]`);
    const CX = 320;
    const OVER = 16; // dépassement de la poignée de chaque côté des vis (mm)
    const set = (node, attrs) => node && Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, v));
    const draw = (v) => {
      const L = v + OVER * 2;
      const x1 = CX - v / 2;
      const x2 = CX + v / 2;
      set(el('handle'), { x: CX - L / 2, width: L });
      set(el('shadow'), { x: CX - L / 2 + 4, width: L - 8 });
      set(el('shine'), { x1: CX - L / 2 + 9, x2: CX + L / 2 - 9 });
      set(el('s1'), { cx: x1 });
      set(el('s2'), { cx: x2 });
      set(el('e1'), { x1, x2: x1 });
      set(el('e2'), { x1: x2, x2 });
      set(el('dim'), { x1, x2 });
    };
    let current = ENTRAXES[parseInt(range.value, 10)];
    let raf;
    const animateTo = (to) => {
      cancelAnimationFrame(raf);
      const from = current;
      const t0 = performance.now();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const step = (t) => {
        const p = reduce ? 1 : Math.min(1, (t - t0) / 350);
        const e = 1 - Math.pow(1 - p, 3);
        current = from + (to - from) * e;
        draw(current);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    const update = (animate = true) => {
      const i = parseInt(range.value, 10);
      const v = ENTRAXES[i];
      root.querySelectorAll('[data-value], [data-value-btn], [data-value-dim]').forEach((n) => (n.textContent = v));
      root.querySelector('[data-hint]').textContent = HINTS[v];
      range.style.setProperty('--p', `${(i / (ENTRAXES.length - 1)) * 100}%`);
      range.setAttribute('aria-valuetext', `${v} millimètres`);
      if (animate) animateTo(v); else { current = v; draw(v); }
      link.href = `${root.dataset.searchUrl}?q=${encodeURIComponent(v + ' mm')}&type=product&options%5Bprefix%5D=last`;
    };
    range.addEventListener('input', () => update(true));
    update(false);
  }


  /* ---------- Recherche de l'en-tête (résultats en direct) ---------- */
  function initHeaderSearch(form) {
    if (form.dataset.ready) return;
    form.dataset.ready = 'true';
    const input = form.querySelector('input[name="q"]');
    const panel = form.querySelector('[data-panel]');
    const results = form.querySelector('[data-results]');
    let controller;
    let timer;
    const close = () => { panel.hidden = true; };
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const term = input.value.trim();
      if (term.length < 2) { close(); return; }
      timer = setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        try {
          const url = `${window.routes?.predictive_search_url || '/search/suggest'}?q=${encodeURIComponent(term)}&resources[type]=product,collection&resources[limit]=6&section_id=bdp-search-results`;
          const html = await (await fetch(url, { signal: controller.signal })).text();
          const box = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bdp-search-results]');
          results.innerHTML = box ? box.innerHTML : '';
          panel.hidden = false;
        } catch (e) { if (e.name !== 'AbortError') console.error(e); }
      }, 200);
    });
    input.addEventListener('focus', () => { if (results.innerHTML.trim() && input.value.trim().length > 1) panel.hidden = false; });
    document.addEventListener('click', (event) => { if (!form.contains(event.target)) close(); });
    form.addEventListener('keydown', (event) => { if (event.key === 'Escape') { close(); input.blur(); } });
  }

  /* ---------- Carte « Trouvez votre poignée » ---------- */
  function initFinder(form) {
    if (form.dataset.ready) return;
    form.dataset.ready = 'true';
    let mode = 'usage';
    form.querySelectorAll('[data-tab]').forEach((tab) => tab.addEventListener('click', () => {
      mode = tab.dataset.tab;
      form.querySelectorAll('[data-tab]').forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
      form.querySelectorAll('[data-panel]').forEach((p) => { p.hidden = p.dataset.panel !== mode; });
    }));
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const search = form.dataset.searchUrl || '/search';
      const toSearch = (q) => `${search}?q=${encodeURIComponent(q)}&type=product&options%5Bprefix%5D=last`;
      if (mode === 'entraxe') {
        const v = form.querySelector('[data-entraxe]').value;
        window.location.href = v ? toSearch(`${v} mm`) : '/collections/poignee-de-meuble';
        return;
      }
      const sel = ['[data-usage]', '[data-material]', '[data-color]'].map((s) => form.querySelector(s)?.selectedOptions[0]);
      const picked = sel.filter((o) => o && o.value);
      // Barre sans onglets : l'entraxe se combine aux autres critères
      const entSel = form.querySelector('[data-entraxe]');
      const ent = !form.querySelector('[data-tab]') && entSel?.value ? `${entSel.value} mm` : '';
      if (!picked.length && !ent) { window.location.href = '/collections/all'; return; }
      if (picked.length === 1 && !ent) { window.location.href = `/collections/${picked[0].value}`; return; }
      window.location.href = toSearch([...picked.map((o) => o.dataset.q), ent].filter(Boolean).join(' '));
    });
  }


  /* ---------- Ajout au panier partagé (ouvre le tiroir panier) ---------- */
  async function bdpAddToCart(items, trigger) {
    const cart = document.querySelector('cart-drawer');
    const body = { items };
    if (cart?.getSectionsToRender) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      cart.setActiveElement?.(trigger || document.activeElement);
    }
    const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'Ajout impossible');
    if (cart?.renderContents) {
      cart.classList.remove('is-empty');
      cart.renderContents(data);
      document.querySelector('.bdp-cart-count')?.classList.add('is-bump');
    } else {
      window.location.href = window.routes?.cart_url || '/cart';
    }
    return data;
  }
  window.bdpAddToCart = bdpAddToCart;

  /* ---------- Fiche produit ---------- */
  function initProduct(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const variants = JSON.parse(root.querySelector('[data-variants]')?.textContent || '[]');
    const form = root.querySelector('[data-bdp-pdp-form]');
    const idInput = form?.querySelector('[data-variant-id]');
    const track = root.querySelector('[data-track]');
    const slides = [...root.querySelectorAll('.bdp-gallery__slide')];
    const thumbs = [...root.querySelectorAll('.bdp-gallery__thumb')];
    const counter = root.querySelector('[data-current]');
    const addBtns = [...root.querySelectorAll('[data-add], [data-sticky-add]')];
    const errorBox = root.querySelector('[data-error]');
    let current = variants.find((v) => String(v.id) === idInput?.value) || variants[0];

    /* Galerie */
    const goTo = (i, smooth = true) => {
      if (!track || !slides[i]) return;
      if (smooth) return track.scrollTo({ left: slides[i].offsetLeft, behavior: 'smooth' });
      // saut instantané : le CSS du rail impose scroll-behavior: smooth, on le neutralise le temps du saut
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = slides[i].offsetLeft;
      requestAnimationFrame(() => { track.style.scrollBehavior = ''; });
    };
    const markActive = (i) => {
      thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
      if (counter) counter.textContent = i + 1;
    };
    track?.addEventListener('scroll', () => {
      const i = Math.round(track.scrollLeft / track.clientWidth);
      markActive(i);
    }, { passive: true });
    thumbs.forEach((t) => t.addEventListener('click', () => goTo(parseInt(t.dataset.index, 10))));
    root.querySelectorAll('[data-nav]').forEach((b) => b.addEventListener('click', () => {
      const i = Math.round(track.scrollLeft / track.clientWidth) + parseInt(b.dataset.nav, 10);
      goTo((i + slides.length) % slides.length);
    }));
    const start = thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true');
    if (start > 0) goTo(start, false);

    /* Variantes */
    const groups = [...root.querySelectorAll('[data-option-index]')];
    const selected = () => groups.map((g) => g.querySelector('input:checked')?.value);
    const refreshAvailability = () => {
      const sel = selected();
      groups.forEach((g, gi) => {
        g.querySelectorAll('.bdp-pill-opt').forEach((label) => {
          const value = label.querySelector('input').value;
          const test = [...sel]; test[gi] = value;
          const match = variants.find((v) => v.options.every((o, k) => o === test[k]));
          label.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    };
    const applyVariant = (v) => {
      current = v;
      if (!v) {
        addBtns.forEach((b) => { b.disabled = true; b.querySelector('[data-add-label]').textContent = 'Indisponible'; });
        return;
      }
      idInput.value = v.id;
      root.querySelectorAll('[data-price]').forEach((el) => (el.textContent = v.price));
      const compare = root.querySelector('[data-compare]');
      const save = root.querySelector('[data-save]');
      if (compare) { compare.hidden = !v.compare; compare.textContent = v.compare || ''; }
      if (save) { save.hidden = !v.save; save.textContent = v.save ? `−${v.save} %` : ''; }
      root.querySelectorAll('[data-variant-title]').forEach((el) => (el.textContent = v.title));
      const sku = root.querySelector('[data-sku]'); if (sku) sku.textContent = v.sku;
      const stock = root.querySelector('[data-stock]');
      if (stock) {
        stock.classList.toggle('is-out', !v.available);
        stock.querySelector('[data-stock-text]').textContent = v.available ? 'En stock' : 'Épuisé';
      }
      addBtns.forEach((b) => { b.disabled = !v.available; b.querySelector('[data-add-label]').textContent = v.available ? 'Ajouter au panier' : 'Épuisé'; });
      groups.forEach((g, gi) => { const out = g.querySelector('[data-option-value]'); if (out) out.textContent = v.options[gi]; });
      if (v.media) {
        const i = slides.findIndex((s) => s.dataset.mediaId === String(v.media));
        if (i > -1) {
          // Saut direct si la photo est loin : un défilement doux sur 20 photos est lent et s'interrompt
          const here = track ? Math.round(track.scrollLeft / (track.clientWidth || 1)) : 0;
          goTo(i, Math.abs(i - here) <= 2);
          markActive(i);
          const strip = thumbs[i]?.parentElement;
          if (strip) {
            const tr = thumbs[i].getBoundingClientRect(), sr = strip.getBoundingClientRect();
            strip.scrollBy({ top: tr.top - sr.top - (sr.height - tr.height) / 2, left: tr.left - sr.left - (sr.width - tr.width) / 2, behavior: 'smooth' });
          }
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url);
      refreshAvailability();
    };
    groups.forEach((g) => g.addEventListener('change', () => {
      const sel = selected();
      applyVariant(variants.find((v) => v.options.every((o, k) => o === sel[k])));
    }));
    if (groups.length) refreshAvailability();

    /* Quantité */
    const qty = root.querySelector('[data-qty-input]');
    root.querySelectorAll('[data-qty]').forEach((b) => b.addEventListener('click', () => {
      qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) + parseInt(b.dataset.qty, 10));
    }));

    /* Ajout */
    const add = async (button) => {
      if (!current) return;
      const label = button.querySelector('[data-add-label]');
      const initial = label.textContent;
      button.classList.add('is-loading');
      label.textContent = 'Ajout…';
      errorBox.hidden = true;
      try {
        await bdpAddToCart([{ id: current.id, quantity: Math.max(1, parseInt(qty?.value, 10) || 1) }], button);
        button.classList.add('is-added');
        label.textContent = 'Ajouté ✓';
        setTimeout(() => { button.classList.remove('is-added'); label.textContent = initial; }, 2000);
      } catch (e) {
        errorBox.textContent = e.message;
        errorBox.hidden = false;
        label.textContent = initial;
      }
      button.classList.remove('is-loading');
    };
    form?.addEventListener('submit', (event) => { event.preventDefault(); add(form.querySelector('[data-add]')); });
    root.querySelector('[data-sticky-add]')?.addEventListener('click', (event) => add(event.currentTarget));

    /* Barre d'achat collante */
    const sticky = root.querySelector('[data-sticky-atc]');
    const mainAdd = form?.querySelector('[data-add]');
    if (sticky && mainAdd && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
      }).observe(mainAdd);
    }

    /* Lien vers le tableau technique */
    root.querySelectorAll('[data-open-specs]').forEach((a) => a.addEventListener('click', () => {
      const d = document.querySelector(a.getAttribute('href'));
      if (d) d.open = true;
    }));
  }

  /* ---------- Recommandations ---------- */
  async function initRelated(section) {
    if (section.dataset.ready) return;
    section.dataset.ready = 'true';
    try {
      const html = await (await fetch(section.dataset.url)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-bdp-related]');
      if (fresh && fresh.innerHTML.trim()) section.innerHTML = fresh.innerHTML;
      else section.remove();
    } catch (e) { console.error(e); }
  }

  /* ---------- Filtres de collection ---------- */
  function initCollection(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const form = root.querySelector('[data-filter-form]');
    const panel = root.querySelector('.bdp-filters');
    const isMobile = () => window.matchMedia('(max-width: 989.98px)').matches;
    const submit = () => {
      const params = new URLSearchParams(new FormData(form));
      [...params.keys()].forEach((k) => { if (params.get(k) === '') params.delete(k); });
      window.location.href = `${form.action}?${params.toString()}`;
    };
    form.addEventListener('submit', (e) => { e.preventDefault(); submit(); });
    form.addEventListener('change', (e) => {
      if (e.target.matches('[data-sort]')) { submit(); return; }
      if (!isMobile() && e.target.type === 'checkbox') submit();
    });
    form.querySelectorAll('.bdp-price-range input').forEach((input) => input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }));
    root.querySelector('[data-filters-open]')?.addEventListener('click', () => { panel.classList.add('is-open'); lockScroll(true); });
    root.querySelectorAll('[data-filters-close]').forEach((b) => b.addEventListener('click', () => { panel.classList.remove('is-open'); lockScroll(false); }));
  }


  /* ---------- Page panier ---------- */
  function initCartPage(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const sectionId = root.dataset.section;
    const errorBox = () => root.querySelector('[data-cart-error]');
    const change = async (line, quantity) => {
      const row = root.querySelector(`[data-line="${line}"]`);
      row?.classList.add('is-loading');
      try {
        const res = await fetch(`${window.routes?.cart_change_url || '/cart/change'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: [sectionId, 'cart-icon-bubble', 'cart-drawer'], sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (data.status) throw new Error(data.description || data.message);
        const parse = (html, sel) => new DOMParser().parseFromString(html, 'text/html').querySelector(sel);
        const fresh = parse(data.sections[sectionId], '[data-bdp-cart-page]');
        if (fresh) { root.innerHTML = fresh.innerHTML; }
        const bubble = document.getElementById('cart-icon-bubble');
        const freshBubble = parse(data.sections['cart-icon-bubble'], '.shopify-section');
        if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;
        const drawer = document.querySelector('#CartDrawer');
        const freshDrawer = parse(data.sections['cart-drawer'], '#CartDrawer');
        if (drawer && freshDrawer) drawer.innerHTML = freshDrawer.innerHTML;
        document.querySelector('cart-drawer')?.classList.toggle('is-empty', data.item_count === 0);
      } catch (e) {
        row?.classList.remove('is-loading');
        const box = errorBox();
        if (box) { box.textContent = e.message || 'Une erreur est survenue.'; box.hidden = false; }
      }
    };
    root.addEventListener('click', (event) => {
      const step = event.target.closest('[data-line-btn]');
      if (step) {
        const line = parseInt(step.dataset.lineBtn, 10);
        const input = root.querySelector(`[data-line-qty="${line}"]`);
        const q = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.step, 10));
        input.value = q;
        change(line, q);
        return;
      }
      const remove = event.target.closest('[data-remove]');
      if (remove) change(parseInt(remove.dataset.remove, 10), 0);
    });
    root.addEventListener('change', (event) => {
      const input = event.target.closest('[data-line-qty]');
      if (input) change(parseInt(input.dataset.lineQty, 10), Math.max(0, parseInt(input.value, 10) || 0));
    });
    let noteTimer;
    root.addEventListener('input', (event) => {
      if (!event.target.matches('[data-cart-note]')) return;
      clearTimeout(noteTimer);
      noteTimer = setTimeout(() => fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: event.target.value }),
      }), 400);
    });
  }


  /* ---------- Codes promo (tiroir + page panier) ---------- */
  async function applyDiscounts(codes, scope, newCode) {
    const cartPage = document.querySelector('[data-bdp-cart-page]');
    const sections = ['cart-drawer', 'cart-icon-bubble'];
    if (cartPage) sections.push(cartPage.dataset.section);
    const res = await fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(','), sections, sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (data.status) throw new Error(data.description || data.message);
    const parse = (html, sel) => html && new DOMParser().parseFromString(html, 'text/html').querySelector(sel);
    const drawer = document.querySelector('#CartDrawer');
    const freshDrawer = parse(data.sections?.['cart-drawer'], '#CartDrawer');
    if (drawer && freshDrawer) drawer.innerHTML = freshDrawer.innerHTML;
    const bubble = document.getElementById('cart-icon-bubble');
    const freshBubble = parse(data.sections?.['cart-icon-bubble'], '.shopify-section');
    if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;
    if (cartPage) {
      const freshPage = parse(data.sections?.[cartPage.dataset.section], '[data-bdp-cart-page]');
      if (freshPage) cartPage.innerHTML = freshPage.innerHTML;
    }
    if (newCode) {
      const found = (data.discount_codes || []).find((d) => d.code.toLowerCase() === newCode.toLowerCase());
      const box = document.querySelector(`[data-bdp-promo="${scope}"]`);
      const msg = box?.querySelector('[data-promo-msg]');
      if (box && msg) {
        box.querySelector('details').open = true;
        msg.hidden = false;
        msg.classList.toggle('is-error', !found || !found.applicable);
        msg.textContent = found && found.applicable
          ? `Code « ${found.code} » appliqué.`
          : 'Ce code n’est pas valide ou ne s’applique pas à votre panier.';
      }
    }
    return data;
  }
  const currentCodes = (box) => [...(box?.querySelectorAll('[data-code]') || [])].map((el) => el.dataset.code);
  const submitPromo = async (box) => {
    const input = box.querySelector('[data-promo-input]');
    const code = input.value.trim();
    if (!code) { input.focus(); return; }
    const btn = box.querySelector('[data-promo-apply]');
    btn.disabled = true;
    btn.textContent = '…';
    try {
      const codes = currentCodes(box).filter((c) => c.toLowerCase() !== code.toLowerCase());
      await applyDiscounts([...codes, code], box.dataset.bdpPromo, code);
    } catch (e) {
      const msg = box.querySelector('[data-promo-msg]');
      if (msg) { msg.hidden = false; msg.classList.add('is-error'); msg.textContent = e.message || 'Une erreur est survenue.'; }
      btn.disabled = false;
      btn.textContent = 'Appliquer';
    }
  };
  document.addEventListener('click', (event) => {
    const apply = event.target.closest('[data-promo-apply]');
    if (apply) { submitPromo(apply.closest('[data-bdp-promo]')); return; }
    const remove = event.target.closest('[data-promo-remove]');
    if (remove) {
      const box = remove.closest('[data-bdp-promo]');
      const codes = currentCodes(box).filter((c) => c !== remove.dataset.promoRemove);
      remove.disabled = true;
      applyDiscounts(codes, box.dataset.bdpPromo).catch((e) => console.error(e));
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.matches('[data-promo-input]')) return;
    event.preventDefault();
    submitPromo(event.target.closest('[data-bdp-promo]'));
  });

  /* ---------- Initialisation ---------- */
  function init(scope = document) {
    scope.querySelectorAll('.bdp-announce').forEach(initAnnouncement);
    initHeader(document.querySelector('.bdp-header'));
    initPanel(document.getElementById('BdpMobileNav'));
    const searchPanel = document.getElementById('BdpSearch');
    if (searchPanel && !searchPanel.dataset.ready) {
      initPanel(searchPanel);
      initSearch(searchPanel);
    }
    scope.querySelectorAll('[data-bdp-vitrine]').forEach(initVitrine);
    scope.querySelectorAll('[data-bdp-quiz]').forEach(initQuiz);
    scope.querySelectorAll('[data-bdp-entraxe]').forEach(initEntraxe);
    initSliders(scope);
    document.querySelectorAll('[data-bdp-hsearch]').forEach(initHeaderSearch);
    scope.querySelectorAll('[data-bdp-finder]').forEach(initFinder);
    scope.querySelectorAll('[data-bdp-product]').forEach(initProduct);
    scope.querySelectorAll('[data-bdp-related]').forEach(initRelated);
    scope.querySelectorAll('[data-bdp-collection]').forEach(initCollection);
    scope.querySelectorAll('[data-bdp-cart-page]').forEach(initCartPage);
    initReveal(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

/* ==========================================================================
   Klaviyo : inscription (API client) + popup de bienvenue + newsletter du footer
   ========================================================================== */
(() => {
  const STORE = 'bdp_wpop';
  const read = () => { try { return JSON.parse(localStorage.getItem(STORE) || '{}'); } catch (e) { return {}; } };
  const write = (v) => { try { localStorage.setItem(STORE, JSON.stringify({ ...read(), ...v })); } catch (e) { /* stockage indisponible */ } };

  // Inscription d'un e-mail à une liste Klaviyo (clé publique, sans secret)
  window.bdpKlaviyoSubscribe = async (key, list, email, source) => {
    const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(key)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
      body: JSON.stringify({ data: { type: 'subscription', attributes: {
        custom_source: source,
        profile: { data: { type: 'profile', attributes: { email, properties: { inscription_source: source, inscription_page: location.pathname } } } },
      }, relationships: { list: { data: { type: 'list', id: list } } } } }),
      keepalive: true,
    });
    if (!res.ok && res.status !== 202) throw new Error(String(res.status));
    write({ subscribed: Date.now() });
  };
  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);

  const initPopup = () => {
    const pop = document.querySelector('[data-bdp-wpop]');
    if (!pop || pop.dataset.ready) return;
    pop.dataset.ready = '1';
    const teaser = document.querySelector('[data-bdp-wpop-teaser]');
    const design = pop.dataset.design === 'true';
    const days = Number(pop.dataset.days || 14);
    const state = read();
    const fromEmail = /[?&](_kx|utm_medium=email)/.test(location.search);
    const blocked = !design && (state.subscribed || fromEmail || (state.closed && Date.now() - state.closed < days * 864e5));
    let lastFocus = null;

    const cookieBannerVisible = () => [...document.querySelectorAll('#shopify-pc__banner, .shopify-pc__banner__wrapper')].some((el) => { const r = el.getBoundingClientRect(); const cs = getComputedStyle(el); return r.width > 0 && r.height > 0 && cs.visibility !== 'hidden' && cs.display !== 'none' && Number(cs.opacity) > 0; });
    const open = (manual = false) => {
      if (!pop.hidden) return;
      // Ne jamais empiler le popup sur le bandeau cookies : on attend la réponse du visiteur
      if (!manual && cookieBannerVisible()) { setTimeout(open, 2500); return; }
      lastFocus = document.activeElement;
      pop.hidden = false;
      if (teaser) teaser.hidden = true;
      pop.offsetWidth; setTimeout(() => pop.classList.add('is-open'), 20);
      document.documentElement.classList.add('bdp-wpop-lock');
      setTimeout(() => pop.querySelector('input[type=email]')?.focus({ preventScroll: true }), 250);
    };
    const close = (remember = true) => {
      pop.classList.remove('is-open');
      document.documentElement.classList.remove('bdp-wpop-lock');
      setTimeout(() => { pop.hidden = true; }, 250);
      if (remember && !read().subscribed) { write({ closed: Date.now() }); if (teaser) teaser.hidden = false; }
      lastFocus?.focus?.({ preventScroll: true });
    };
    pop.querySelectorAll('[data-wpop-close]').forEach((b) => b.addEventListener('click', () => close()));
    document.addEventListener('keydown', (e) => {
      if (pop.hidden) return;
      if (e.key === 'Escape') close();
      if (e.key === 'Tab') { // garde le focus dans la fenêtre
        const f = [...pop.querySelectorAll('button, a[href], input')].filter((el) => el.offsetParent);
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    });
    teaser?.addEventListener('click', () => open(true));

    const form = pop.querySelector('[data-wpop-form]');
    const err = pop.querySelector('[data-wpop-error]');
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = form.email.value.trim();
      err.hidden = true;
      if (!validEmail(email)) { err.textContent = 'Merci de saisir une adresse e-mail valide.'; err.hidden = false; form.email.focus(); return; }
      const btn = form.querySelector('button');
      btn.disabled = true; btn.classList.add('is-loading');
      try {
        await window.bdpKlaviyoSubscribe(pop.dataset.key, pop.dataset.list, email, 'Popup bienvenue');
        pop.querySelector('[data-wpop-step="form"]').hidden = true;
        pop.querySelector('[data-wpop-step="done"]').hidden = false;
        const code = pop.querySelector('[data-wpop-apply]')?.dataset.wpopApply;
        if (code) {
          // /discount/CODE pose le cookie de remise utilisé au paiement ; on l'ajoute aussi au panier en cours
          Promise.allSettled([
            fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }),
            fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: code }) }),
          ]).then(() => { const n = pop.querySelector('[data-wpop-applied]'); if (n) n.hidden = false; });
        }
        if (teaser) teaser.remove();
      } catch (x) {
        err.textContent = 'Oups, l\'inscription n\'a pas abouti. Réessayez dans un instant.'; err.hidden = false;
      } finally { btn.disabled = false; btn.classList.remove('is-loading'); }
    });
    pop.querySelector('[data-wpop-copy]')?.addEventListener('click', (e) => {
      const b = e.currentTarget;
      navigator.clipboard?.writeText(b.dataset.wpopCopy).then(() => { b.querySelector('small').textContent = 'Copié !'; });
    });

    if (design) return; // dans l'éditeur : affichage à la demande via la sélection de la section
    if (blocked) { if (teaser && state.closed && !state.subscribed && !fromEmail) teaser.hidden = false; return; }
    const timer = setTimeout(open, Number(pop.dataset.delay || 10) * 1000);
    if (pop.dataset.exit === 'true' && matchMedia('(pointer: fine)').matches) {
      const onLeave = (e) => { if (e.clientY <= 0 && !e.relatedTarget) { clearTimeout(timer); open(); document.removeEventListener('mouseout', onLeave); } };
      setTimeout(() => document.addEventListener('mouseout', onLeave), 4000);
    }
  };

  // Newsletter du footer : inscription Klaviyo en plus du formulaire Shopify
  const initFooter = () => {
    const form = document.getElementById('BdpFooterNewsletter');
    const pop = document.querySelector('[data-bdp-wpop]');
    const key = form?.dataset.klaviyoKey || pop?.dataset.key;
    const list = form?.dataset.klaviyoList || pop?.dataset.list;
    if (!form || !key || !list || form.dataset.kReady) return;
    form.dataset.kReady = '1';
    form.addEventListener('submit', (e) => {
      const email = form.querySelector('input[type=email]')?.value.trim();
      if (!email || !validEmail(email) || form.dataset.kSent) return;
      e.preventDefault();
      form.dataset.kSent = '1';
      window.bdpKlaviyoSubscribe(key, list, email, 'Newsletter footer').catch(() => {}).finally(() => form.submit());
    });
  };

  const run = () => { initPopup(); initFooter(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  document.addEventListener('shopify:section:load', run);
  document.addEventListener('shopify:section:select', (e) => {
    const pop = e.target.querySelector?.('[data-bdp-wpop]');
    if (pop) { pop.hidden = false; setTimeout(() => pop.classList.add('is-open'), 20); }
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const pop = e.target.querySelector?.('[data-bdp-wpop]');
    if (pop) { pop.classList.remove('is-open'); pop.hidden = true; }
  });
})();
